import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, styled, keyframes } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { rateMyTradeAPI } from '../utils/api';
import TradePackagesHeader from './TradePackages/TradePackagesHeader';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled(Box)({
  minHeight: '100vh',
  background: '#FFFFFF',
  position: 'relative',
  padding: '80px 20px 120px',
  '@media (max-width: 600px)': {
    padding: '60px 16px 80px',
  },
});

const ContentBox = styled(Box)({
  maxWidth: '560px',
  width: '100%',
  margin: '0 auto',
  textAlign: 'center',
  paddingTop: '80px',
  animation: `${fadeIn} 0.8s cubic-bezier(0.16, 1, 0.3, 1)`,
  '@media (max-width: 600px)': {
    paddingTop: '48px',
  },
});

const VerifyMagicLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyMagicLink, isAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(true);
  const hasVerified = useRef(false);

  useEffect(() => {
    const verify = async () => {
      // Prevent double verification in React StrictMode or on re-renders
      if (hasVerified.current) {
        return;
      }
      hasVerified.current = true;

      // A returnUrl carried in the magic-link URL survives across browsers —
      // mobile email apps open the link in a different context than where the
      // email was entered, so localStorage isn't shared. Only honor internal
      // same-origin paths.
      const rawReturn = new URLSearchParams(window.location.search).get('returnUrl');
      const queryReturnUrl = rawReturn && /^\/(?!\/)/.test(rawReturn) ? rawReturn : null;

      try {
        const response = await verifyMagicLink(token);

        // Pending Monte Carlo save — send them back to the simulator to auto-save.
        const pendingMonteCarlo = localStorage.getItem('pendingMonteCarlo');
        if (pendingMonteCarlo) {
          try {
            const { sleeperLeagueId, username } = JSON.parse(pendingMonteCarlo);
            if (sleeperLeagueId) {
              const params = new URLSearchParams({ restorePending: '1' });
              if (username) params.set('username', username);
              navigate(`/simulate/${sleeperLeagueId}?${params.toString()}`);
              return;
            }
          } catch (e) { /* fall through to default */ }
        }

        // Check for pending Rate My Trade claim — link the user's Sleeper account
        const pendingTradeClaim = localStorage.getItem('pendingRateMyTradeClaim');
        if (pendingTradeClaim) {
          try {
            const { sleeperUserId, sleeperUsername } = JSON.parse(pendingTradeClaim);
            if (sleeperUserId) {
              await rateMyTradeAPI.claim({ sleeperUserId, sleeperUsername });
            }
          } catch (e) {
            // Non-fatal: surface in dashboard if linking failed
            console.warn('Failed to claim trades:', e);
          }
          localStorage.removeItem('pendingRateMyTradeClaim');
          navigate('/rate-my-trade');
          return;
        }

        // Check for startup listing form data (user was listing a startup) - HIGHEST PRIORITY
        const startupListingData = localStorage.getItem('startupListingFormData');
        if (startupListingData) {
          // Keep the data - the StartupListingForm will handle submission on mount
          navigate('/startup-marketplace/list');
          return;
        }

        // Check for orphan listing form data (user was listing an orphan) - HIGHEST PRIORITY
        const orphanListingData = localStorage.getItem('orphanListingFormData');
        if (orphanListingData) {
          // Keep the data - the OrphanListingForm will handle submission on mount
          navigate('/orphan-marketplace/list');
          return;
        }

        // Check for login return URL (e.g., user was applying for orphan team)
        const loginReturnUrl = queryReturnUrl || localStorage.getItem('loginReturnUrl');
        if (loginReturnUrl) {
          localStorage.removeItem('loginReturnUrl');
          navigate(loginReturnUrl);
          return;
        }

        // Check for pending package link (user created package but needed to verify email)
        const pendingPackageLink = localStorage.getItem('pendingPackageLink');
        if (pendingPackageLink) {
          localStorage.removeItem('pendingPackageLink');
          // Redirect directly to the package view page
          navigate(`/trade-packages/${pendingPackageLink}`);
          return;
        }

        // Check for pending package trades (need to create package after auth)
        const pendingTrades = localStorage.getItem('pendingPackageTrades');
        const pendingContext = localStorage.getItem('pendingPackageContext');
        if (pendingTrades && pendingContext) {
          localStorage.removeItem('pendingPackageTrades');
          localStorage.removeItem('pendingPackageContext');
          const context = JSON.parse(pendingContext);
          navigate(`/trade-packages/canvas/${context.leagueId}/${context.recipientId}`, {
            state: { restorePackage: true, packageTrades: JSON.parse(pendingTrades) }
          });
          return;
        }

        // Check if there's orphan team context (legacy approach)
        if (response.orphanTeamId && response.orphanApplicationAction === 'adopt') {
          // Clean up orphan context from localStorage
          localStorage.removeItem('orphanAdoptionContext');
          // Redirect to orphan adoption application page
          navigate(`/orphan-marketplace/${response.orphanTeamId}/apply`);
          return;
        }

        if (response.orphanTeamId && response.orphanApplicationAction === 'list') {
          // Clean up orphan context from localStorage
          localStorage.removeItem('orphanAdoptionContext');
          // Redirect to orphan listing form
          navigate('/orphan-marketplace/list');
          return;
        }

        // Check if there's a package link associated with this verification
        if (response.packageUniqueLink) {
          // Redirect directly to the package view page
          navigate(`/trade-packages/${response.packageUniqueLink}`);
        } else {
          // Default landing for any clean sign-in — the shell Home
          navigate('/home');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired link');
        setVerifying(false);
      }
    };

    if (token) {
      verify();
    }
  }, [token, verifyMagicLink, navigate]);

  return (
    <PageContainer>
      <TradePackagesHeader />
      <ContentBox>
        {verifying ? (
          <>
            <CircularProgress
              size={40}
              thickness={2}
              sx={{
                color: '#1D1D1F',
                marginBottom: '32px',
              }}
            />
            <Typography
              sx={{
                fontSize: '21px',
                fontWeight: 400,
                color: '#6E6E73',
                lineHeight: 1.38,
                letterSpacing: '0.011em',
              }}
            >
              Verifying your link
            </Typography>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: '48px',
                fontWeight: 600,
                color: '#1D1D1F',
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                marginBottom: '24px',
                '@media (max-width: 600px)': {
                  fontSize: '40px',
                },
              }}
            >
              Link expired.
            </Typography>
            <Typography
              sx={{
                fontSize: '21px',
                fontWeight: 400,
                color: '#6E6E73',
                lineHeight: 1.38,
                letterSpacing: '0.011em',
                marginBottom: '64px',
                '@media (max-width: 600px)': {
                  fontSize: '19px',
                  marginBottom: '48px',
                },
              }}
            >
              This link is no longer valid. Request a new one to continue.
            </Typography>
          </>
        )}
      </ContentBox>
    </PageContainer>
  );
};

export default VerifyMagicLink;
